import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import { agGridThemes } from '../../../@shared/ag-grid-themes';

/**
 * Popup editor for Scanners.<id>.Stream[] — IT-F2-406 cid a99fdac3 (initial)
 * + cid 29bbc2d8 (refactor to popup+ag-grid per Mike c/12c4aa7f 2026-10-18).
 *
 * Two-level modal flow:
 *   outer modal → AG-Grid list of all Stream entries + Add / Done
 *   inner modal → single-entry form (opens on row-click or Add)
 *
 * Public API — set by scanners.component to drive open/close + round-trip
 * the array back to the Scanner doc:
 *   <app-stream-editor
 *     [showModal]="showStreamEditor"
 *     [stream]="editScanner.Stream"
 *     [scannerId]="editScanner.Id"
 *     (streamChange)="editScanner.Stream = $event"
 *     (closeModal)="showStreamEditor = false"></app-stream-editor>
 *
 * The outer modal mounts whenever showModal is true. Changes emit on every
 * mutation so a parent save path can commit without waiting for a Done
 * click; Done is purely a UX exit.
 */
export interface StreamEntry {
  Enabled?: boolean;
  Collection?: string;
  Channel?: string;
  WsPath?: string;
  RestPath?: string;
  KeyField?: string;
  SymbolListName?: string;
  SkipPremiumFloor?: boolean;
  Append?: boolean;
  Legacy?: boolean;
  SourceDb?: string;
  SourceDbPattern?: string;
}

@Component({
  standalone: true,
  selector: 'app-stream-editor',
  imports: [CommonModule, FormsModule, AgGridAngular],
  templateUrl: './stream-editor.component.html',
})
export class StreamEditorComponent implements OnChanges {
  @Input() stream: StreamEntry[] | StreamEntry | null = null;
  @Input() scannerId: string = '';
  @Input() showModal: boolean = false;
  @Output() streamChange = new EventEmitter<StreamEntry[] | null>();
  @Output() closeModal = new EventEmitter<void>();

  entries: StreamEntry[] = [];

  // Sub-modal state. null → sub-modal closed; otherwise index into entries
  // (or -1 for a new entry pending insertion on save).
  editingIndex: number | null = null;
  editingDraft: StreamEntry = {};
  // Advanced panel visibility within the sub-modal — auto-opens when the
  // draft uses an advanced field so admins see what's in play without
  // hunting through the collapsed section.
  draftExpandedExtras: boolean = false;

  gridTheme = agGridThemes.dark;
  gridOptions: any = {
    headerHeight: 30,
    rowHeight: 32,
    suppressCellFocus: true,
    overlayNoRowsTemplate: '<span>No entries</span>',
    // IT-F2-406 cid 067b02ee — Mike c/449ed243 said the up/down arrows
    // and separate row-strip were still there. Native row-drag replaces
    // both the up/down buttons AND the separate strip; the drag handle
    // column (rowDrag: true) gives a grabbable icon and rowDragManaged
    // auto-reorders rowData, which is this.entries.
    rowDragManaged: true,
    animateRows: true,
    defaultColDef: { sortable: false, resizable: true, filter: false, suppressMovable: true },
    columnDefs: [
      { headerName: '', rowDrag: true, width: 36, maxWidth: 36,
        suppressSizeToFit: true, cellClass: 'cursor-grab' },
      { headerName: '#', valueGetter: (p: any) => (p.node?.rowIndex ?? 0) + 1, width: 54, maxWidth: 54, cellClass: 'font-mono text-xs' },
      { headerName: 'On', field: 'Enabled', width: 60, maxWidth: 60,
        cellRenderer: (p: any) => p?.data?.Enabled === false
          ? '<i class="fa-solid fa-circle-xmark text-red-500"></i>'
          : '<i class="fa-solid fa-circle-check text-emerald-500"></i>' },
      { headerName: 'Collection', field: 'Collection', minWidth: 160, flex: 1, cellClass: 'font-mono text-xs' },
      { headerName: 'Channel', field: 'Channel', minWidth: 140, flex: 1, cellClass: 'font-mono text-xs' },
      { headerName: 'WsPath', field: 'WsPath', minWidth: 140, flex: 1, cellClass: 'font-mono text-xs' },
      { headerName: 'RestPath', field: 'RestPath', minWidth: 140, flex: 1, cellClass: 'font-mono text-xs' },
      { headerName: 'KeyField', field: 'KeyField', minWidth: 90, maxWidth: 110, cellClass: 'font-mono text-xs' },
      { headerName: 'Flags', valueGetter: (p: any) => this._flagChips(p?.data), minWidth: 120, flex: 1, cellClass: 'font-mono text-xs text-amber-500' },
    ],
  };

  ngOnChanges(_: SimpleChanges) {
    this._load();
  }

  private _load() {
    const normalized: StreamEntry[] = Array.isArray(this.stream)
      ? [...this.stream]
      : (this.stream && typeof this.stream === 'object')
        ? [this.stream as StreamEntry]
        : [];
    this.entries = normalized.map(e => ({ ...e }));
  }

  private _flagChips(e: StreamEntry | undefined): string {
    if (!e) return '';
    const flags: string[] = [];
    if (e.Append) flags.push('append');
    if (e.Legacy) flags.push('legacy');
    if (e.SkipPremiumFloor) flags.push('skipFloor');
    if (e.SymbolListName) flags.push('sym:' + e.SymbolListName);
    if (e.SourceDbPattern) flags.push('srcPat');
    else if (e.SourceDb) flags.push('src:' + e.SourceDb);
    return flags.join(' · ');
  }

  // --- Outer modal: list CRUD ---

  onDone() {
    this.closeModal.emit();
  }

  onRowClicked(ev: any) {
    const i = ev?.node?.rowIndex;
    if (typeof i !== 'number' || i < 0 || i >= this.entries.length) return;
    this._openEdit(i);
  }

  openAdd() {
    this._openEdit(-1);
  }

  // IT-F2-406 cid 067b02ee — rowDragManaged mutates this.entries in-place
  // on drop; emit the reordered array so the parent save path picks up
  // the new order. The event fires once per drop (not per hover).
  onRowDragEnd(_: any) {
    this._emit();
  }

  // Delete lives inside the single-entry sub-modal now (cid 067b02ee).
  // The outer grid is read-only on destructive actions; row click = edit,
  // drag = reorder, delete button = inside the open edit modal.
  deleteFromEdit() {
    if (this.editingIndex === null) return;
    if (this.editingIndex === -1) {   // "new entry" draft never persisted
      this.cancelEdit();
      return;
    }
    this.entries.splice(this.editingIndex, 1);
    this.editingIndex = null;
    this.editingDraft = {};
    this._emit();
  }

  // --- Sub-modal: single-entry form ---

  private _openEdit(index: number) {
    this.editingIndex = index;
    this.editingDraft = index === -1
      ? { Enabled: true, KeyField: 'symbol' }
      : { ...this.entries[index] };
    this.draftExpandedExtras = !!(
      this.editingDraft.Append || this.editingDraft.Legacy ||
      this.editingDraft.SymbolListName || this.editingDraft.SkipPremiumFloor ||
      this.editingDraft.SourceDb || this.editingDraft.SourceDbPattern
    );
  }

  cancelEdit() {
    this.editingIndex = null;
    this.editingDraft = {};
  }

  saveEdit() {
    if (this.editingIndex === null) return;
    if (this.draftCollectionError()) return;  // required-field block
    if (this.draftWsPathError()) return;
    if (this.draftRestPathError()) return;
    const stripped = this._stripEmpty(this.editingDraft);
    if (this.editingIndex === -1) {
      this.entries.push(stripped);
    } else {
      this.entries[this.editingIndex] = stripped;
    }
    this.editingIndex = null;
    this.editingDraft = {};
    this._emit();
  }

  toggleDraftExtras() {
    this.draftExpandedExtras = !this.draftExpandedExtras;
  }

  // --- Shared helpers ---

  private _emit() {
    const out = this.entries
      .map(e => this._stripEmpty(e))
      .filter(e => Object.keys(e).length > 0);
    this.streamChange.emit(out.length ? out : null);
  }

  private _stripEmpty(e: StreamEntry): StreamEntry {
    const out: StreamEntry = {};
    const STRING_KEYS: (keyof StreamEntry)[] = [
      'Collection', 'Channel', 'WsPath', 'RestPath', 'KeyField',
      'SymbolListName', 'SourceDb', 'SourceDbPattern',
    ];
    const BOOL_KEYS: (keyof StreamEntry)[] = [
      'Enabled', 'SkipPremiumFloor', 'Append', 'Legacy',
    ];
    for (const k of STRING_KEYS) {
      const v = e?.[k];
      if (typeof v === 'string' && v.trim()) (out as any)[k] = v.trim();
    }
    for (const k of BOOL_KEYS) {
      const v = e?.[k];
      if (typeof v === 'boolean') (out as any)[k] = v;
    }
    return out;
  }

  // --- Draft validation (only applies while sub-modal is open) ---

  draftCollectionError(): string {
    return (this.editingDraft.Collection || '').trim() ? '' : 'required';
  }

  // Uniqueness across OTHER rows (excludes the one being edited).
  draftWsPathError(): string {
    const v = (this.editingDraft.WsPath || '').trim();
    if (!v) return '';
    for (let i = 0; i < this.entries.length; i++) {
      if (i === this.editingIndex) continue;
      if ((this.entries[i].WsPath || '').trim() === v) return 'duplicate WsPath';
    }
    return '';
  }

  draftRestPathError(): string {
    const v = (this.editingDraft.RestPath || '').trim();
    if (!v) return '';
    for (let i = 0; i < this.entries.length; i++) {
      if (i === this.editingIndex) continue;
      if ((this.entries[i].RestPath || '').trim() === v) return 'duplicate RestPath';
    }
    return '';
  }

  // Enabled on the Save button in the sub-modal.
  canSaveDraft(): boolean {
    return !this.draftCollectionError() && !this.draftWsPathError() && !this.draftRestPathError();
  }

  // Backdrop click handler. Only closes when the click lands directly
  // on the backdrop (not bubbled from inside the card). Always stops
  // propagation so the enclosing Scanner-edit modal's own backdrop
  // handler doesn't also fire and dismiss the parent.
  onBackdropClick(ev: MouseEvent, source: 'outer' | 'inner') {
    ev.stopPropagation();
    if (ev.target !== ev.currentTarget) return;
    if (source === 'inner') this.cancelEdit();
    else this.closeModal.emit();
  }
}
